import { memo, useMemo } from 'react';
import dompurify from 'dompurify';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';

const ALLOWED_IFRAME_SOURCES = [
	'https://www.youtube.com',
	'https://www.youtube-nocookie.com',
	'https://fast.wistia.net',
];

const CustomHtmlCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
	}) => {
		const cardData = card.data || {};
		const title = cardData.title || 'Custom HTML';
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#666666';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 1;
		const htmlContent = cardData.htmlContent || '<p>No content</p>';
		const hideTitle = cardData.hideTitle || false;
		const removePadding = cardData.removePadding || false;

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		const htmlDecode = (input) => {
			let returnValue = input.replaceAll('&gt;', '>');
			returnValue = returnValue.replaceAll('&lt;', '<');
			returnValue = returnValue.replaceAll('&quot;', '"');
			returnValue = returnValue.replaceAll('&apos;', "'");
			returnValue = returnValue.replaceAll('&amp;', '&');
			return returnValue;
		};

		const sanitizedContent = useMemo(() => {
			const decodedHtml = htmlDecode(htmlContent);

			const purify = dompurify;

			purify.addHook('uponSanitizeElement', (node, data) => {
				if (data.tagName === 'iframe') {
					const src = node.getAttribute('src');
					if (!src) {
						node.remove();
						return;
					}

					let isAllowed = false;
					try {
						const srcOrigin = new URL(src).origin;
						isAllowed = ALLOWED_IFRAME_SOURCES.includes(srcOrigin);
					} catch {
						isAllowed = false;
					}

					if (!isAllowed) {
						node.remove();
					}
				}
			});

			try {
				return purify.sanitize(decodedHtml, {
					ADD_TAGS: ['iframe'],
					ADD_ATTR: [
						'src',
						'width',
						'height',
						'frameborder',
						'allowfullscreen',
						'allow',
						'target',
						'rel',
					],
				});
			} finally {
				purify.removeHook('uponSanitizeElement');
			}
		}, [htmlContent]);

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					padding: removePadding ? '0' : '16px',
				}}
			>
				{!hideTitle && (
					<div className="d-card-head">
						<h4
							style={{
								fontSize: '14px',
								fontWeight: 700,
								lineHeight: '18px',
								margin: '0 0 10px',
								color: textColor,
							}}
						>
							{title}
						</h4>
					</div>
				)}

				<div
					className="flex-1"
					style={{
						overflowY: 'auto',
						overflowX: 'hidden',
						minHeight: 0,
						borderRadius: removePadding ? '14px' : '0',
					}}
				>
					<div
						dangerouslySetInnerHTML={{
							__html: sanitizedContent,
						}}
						style={{
							width: '100%',
							height: '100%',
						}}
					/>
				</div>
			</div>
		);
	}
);

CustomHtmlCard.displayName = 'CustomHtmlCard';

export default CustomHtmlCard;
